'use client';
import React, { useState, useRef } from 'react';
import {
  Search, QrCode, UserCheck, UserX, Clock, Car,
  AlertTriangle, CheckCircle, Shield, Camera,
  ChevronDown, X, Printer, Phone, CreditCard,
  Users, DoorOpen, LogIn, LogOut, FileWarning,
} from 'lucide-react';
import { mockVisitors, mockVisits, mockBlacklist, type Visitor, type Visit } from '@/data/mockData';
import { StatusBadge, TypeBadge } from '@/components/ui/StatusBadge';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { Toaster, toast } from 'sonner';

function formatTime(iso: string): string {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const yy = d.getFullYear();
  return `${dd}/${mo}/${yy}`;
}

function computeDuration(entryIso: string): string {
  const entry = new Date(entryIso).getTime();
  const now = new Date('2026-08-09T11:12:07').getTime();
  const diffMs = now - entry;
  const totalMins = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  if (hours === 0) return `${mins} دقيقة`;
  if (mins === 0) return `${hours} ساعة`;
  return `${hours} ساعة ${mins} دقيقة`;
}

type GateTab = 'entry' | 'exit';
type SearchMode = 'text' | 'qr';

export default function GateScreenContent() {
  const [activeTab, setActiveTab] = useState<GateTab>('entry');
  const [searchMode, setSearchMode] = useState<SearchMode>('text');
  const [searchQuery, setSearchQuery] = useState('');
  const [foundVisitor, setFoundVisitor] = useState<Visitor | null>(null);
  const [selectedGate, setSelectedGate] = useState<string>('البوابة الرئيسية');
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [passengersCount, setPassengersCount] = useState('1');
  const [safetyAcknowledged, setSafetyAcknowledged] = useState(false);
  const [badgeNumber, setBadgeNumber] = useState('');
  const [purpose, setPurpose] = useState('');
  const [showBlacklistAlert, setShowBlacklistAlert] = useState(false);
  const [showSafetyModal, setShowSafetyModal] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState<Visit | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const currentlyInside = mockVisits.filter((v) => v.status === 'داخل' || v.status === 'منتهية الصلاحية');
  const overstayCount = mockVisits.filter((v) => v.status === 'منتهية الصلاحية').length;

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    setSearchPerformed(true);
    const q = searchQuery.trim();
    const visitor = mockVisitors.find(
      (v) => v.nationalId === q || v.mobile === q || v.name.includes(q)
    );
    setFoundVisitor(visitor ?? null);
    setSafetyAcknowledged(false);
    setVehiclePlate('');
    setPurpose('');
    setBadgeNumber('');
    if (visitor?.isBlacklisted) {
      setShowBlacklistAlert(true);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setFoundVisitor(null);
    setSearchPerformed(false);
    setSafetyAcknowledged(false);
    setShowBlacklistAlert(false);
    searchRef.current?.focus();
  };

  const handleRegisterEntry = () => {
    if (!safetyAcknowledged) {
      setShowSafetyModal(true);
      return;
    }
    if (!badgeNumber.trim()) {
      toast.error('يجب إدخال رقم الكارت المُسلَّم للزائر');
      return;
    }
    setIsSubmitting(true);
    // TODO: Backend — POST /api/visits with { visitorId, gate, vehiclePlate, passengersCount, badgeNumber, purpose, safetyAcknowledged: true, entryTime: now }
    setTimeout(() => {
      setIsSubmitting(false);
      toast.success(`تم تسجيل دخول ${foundVisitor?.name} بنجاح — كارت رقم ${badgeNumber}`, {
        duration: 4000,
      });
      handleClearSearch();
    }, 1200);
  };

  const handleConfirmExit = (visit: Visit) => {
    setIsSubmitting(true);
    // TODO: Backend — PATCH /api/visits/:id with { exitTime: now, status: 'خرج', badgeReturned: true }
    setTimeout(() => {
      setIsSubmitting(false);
      setShowExitConfirm(null);
      toast.success(`تم تسجيل خروج ${visit.visitorName} — مدة الزيارة: ${computeDuration(visit.entryTime)}`, {
        duration: 4000,
      });
    }, 1000);
  };

  const blacklistEntry = foundVisitor
    ? mockBlacklist.find((b) => b.nationalId === foundVisitor.nationalId)
    : null;

  const gates = ['البوابة الرئيسية', 'بوابة الشاحنات', 'البوابة الخلفية'];
  const purposes = ['اجتماع عمل', 'توريد مواد', 'صيانة', 'تفتيش', 'زيارة رسمية', 'تسليم شحنة'];

  return (
    <div className="min-h-screen bg-background p-4 xl:p-6" dir="rtl">
      <Toaster position="bottom-right" richColors />

      {/* Header bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent/15 border border-accent/30 flex items-center justify-center">
            <DoorOpen size={20} className="text-accent" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">شاشة البوابة</h1>
            <p className="text-sm text-muted-foreground">الوردية الصباحية — عمر سالم</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-card border border-border rounded-xl px-3 py-2">
            <Clock size={14} className="text-accent" />
            <span className="text-sm font-semibold text-foreground tabular-nums">11:12</span>
            <span className="text-xs text-muted-foreground">09/08/2026</span>
          </div>
          <div className="flex items-center gap-2 bg-success/10 border border-success/30 rounded-xl px-3 py-2">
            <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
            <span className="text-sm font-semibold text-success tabular-nums">{currentlyInside.length} داخل</span>
          </div>
          {overstayCount > 0 && (
            <div className="flex items-center gap-2 bg-warning/10 border border-warning/30 rounded-xl px-3 py-2">
              <AlertTriangle size={14} className="text-warning" />
              <span className="text-sm font-semibold text-warning tabular-nums">{overstayCount} منتهية</span>
            </div>
          )}
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-2 mb-6 bg-card border border-border rounded-2xl p-1.5 w-fit">
        <button
          onClick={() => setActiveTab('entry')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-base transition-all duration-200 ${
            activeTab === 'entry' ?'bg-accent text-accent-foreground shadow-lg' :'text-muted-foreground hover:text-foreground'
          }`}
          suppressHydrationWarning
        >
          <LogIn size={18} />
          تسجيل دخول
        </button>
        <button
          onClick={() => setActiveTab('exit')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-base transition-all duration-200 ${
            activeTab === 'exit' ?'bg-accent text-accent-foreground shadow-lg' :'text-muted-foreground hover:text-foreground'
          }`}
          suppressHydrationWarning
        >
          <LogOut size={18} />
          تسجيل خروج
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left: Main form area */}
        <div className="xl:col-span-2 space-y-4">

          {/* Entry Tab */}
          {activeTab === 'entry' && (
            <div className="space-y-4">
              {/* Search modes */}
              <div className="bg-card border border-border rounded-2xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-bold text-foreground text-lg">بحث عن الزائر</h2>
                  <div className="flex gap-1 bg-muted rounded-xl p-1">
                    <button
                      suppressHydrationWarning
                      onClick={() => setSearchMode('text')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                        searchMode === 'text' ? 'bg-card text-foreground shadow' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Search size={14} />
                      نصي
                    </button>
                    <button
                      suppressHydrationWarning
                      onClick={() => setSearchMode('qr')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                        searchMode === 'qr' ? 'bg-card text-foreground shadow' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <QrCode size={14} />
                      QR
                    </button>
                  </div>
                </div>

                {searchMode === 'text' ? (
                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <Search size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        ref={searchRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        placeholder="الرقم القومي أو رقم الموبايل أو الاسم..."
                        className="w-full bg-input border border-border rounded-xl pr-11 pl-4 py-4 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-accent transition-all"
                        suppressHydrationWarning
                      />
                      {searchQuery && (
                        <button
                          onClick={handleClearSearch}
                          suppressHydrationWarning
                          className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                    <button
                      onClick={handleSearch}
                      suppressHydrationWarning
                      className="px-6 py-4 bg-accent text-accent-foreground rounded-xl font-bold text-base hover:opacity-90 transition-all duration-150 active:scale-95 whitespace-nowrap"
                    >
                      بحث
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-xl p-8 gap-3">
                    <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center">
                      <QrCode size={32} className="text-accent" />
                    </div>
                    <p className="text-foreground font-semibold">امسح رمز QR الخاص بالزيارة</p>
                    <p className="text-sm text-muted-foreground text-center">وجّه الكاميرا نحو رمز QR المطبوع على تصريح الزيارة</p>
                    <button
                      onClick={() => {
                        setSearchQuery('29801234567891');
                        setSearchMode('text');
                        setTimeout(handleSearch, 100);
                      }}
                      suppressHydrationWarning
                      className="mt-2 px-4 py-2 bg-accent/15 border border-accent/30 text-accent rounded-xl text-sm font-medium hover:bg-accent/25 transition-all"
                    >
                      {/* TODO: Integrate real QR camera scanner */}
                      محاكاة مسح QR
                    </button>
                  </div>
                )}

                {/* Search hint */}
                {!searchPerformed && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                    <Shield size={12} />
                    <span>ابحث بالرقم القومي (14 رقم) أو رقم الموبايل أو الاسم الكامل</span>
                  </div>
                )}

                {/* Not found */}
                {searchPerformed && !foundVisitor && (
                  <div className="mt-4 p-4 bg-muted/50 border border-border rounded-xl flex items-center gap-3">
                    <UserX size={20} className="text-muted-foreground shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-foreground">لم يُعثر على الزائر</p>
                      <p className="text-xs text-muted-foreground mt-0.5">يمكنك تسجيل زائر جديد بملء البيانات يدوياً أدناه</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Blacklist alert */}
              {showBlacklistAlert && foundVisitor?.isBlacklisted && (
                <div className="bg-danger/10 border-2 border-danger rounded-2xl p-5 fade-in">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-danger/20 flex items-center justify-center shrink-0">
                      <FileWarning size={20} className="text-danger" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-danger text-lg">⚠️ تحذير — زائر ممنوع من الدخول</h3>
                      <p className="text-sm text-foreground mt-1 font-semibold">{foundVisitor.name}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        <span className="font-medium text-foreground">سبب المنع: </span>
                        {blacklistEntry?.reason}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        أُضيف بواسطة: {blacklistEntry?.addedBy} — بتاريخ {blacklistEntry ? formatDate(blacklistEntry.addedDate) : ''}
                      </p>
                    </div>
                    <button onClick={() => setShowBlacklistAlert(false)} suppressHydrationWarning className="text-muted-foreground hover:text-foreground">
                      <X size={16} />
                    </button>
                  </div>
                  <div className="mt-4 p-3 bg-danger/5 border border-danger/20 rounded-xl">
                    <p className="text-sm text-danger font-semibold">يُمنع منعاً باتاً السماح لهذا الشخص بالدخول. راجع المشرف الأمني فوراً.</p>
                  </div>
                </div>
              )}

              {/* Visitor card */}
              {foundVisitor && !foundVisitor.isBlacklisted && (
                <div className="bg-card border border-border rounded-2xl p-5 fade-in space-y-5">
                  {/* Visitor info header */}
                  <div className="flex items-start gap-4">
                    <div className="relative">
                      <div className="w-16 h-16 rounded-2xl bg-muted border border-border overflow-hidden">
                        {/* TODO: Load visitor photo from Supabase Storage */}
                        <div className="w-full h-full flex items-center justify-center bg-accent/10">
                          <UserCheck size={28} className="text-accent" />
                        </div>
                      </div>
                      <div className="absolute -bottom-1 -left-1 w-5 h-5 bg-success rounded-full border-2 border-card flex items-center justify-center">
                        <CheckCircle size={10} className="text-success-foreground" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-bold text-foreground">{foundVisitor.name}</h3>
                        <TypeBadge type={foundVisitor.type} size="sm" />
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">{foundVisitor.company}</p>
                      <div className="flex items-center gap-4 mt-2 flex-wrap">
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <CreditCard size={12} />
                          {foundVisitor.nationalId}
                        </span>
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Phone size={12} />
                          {foundVisitor.mobile}
                        </span>
                      </div>
                    </div>
                    <button className="flex items-center gap-1.5 text-xs text-accent border border-accent/30 bg-accent/10 rounded-lg px-3 py-1.5 hover:bg-accent/20 transition-all" suppressHydrationWarning>
                      <Camera size={12} />
                      التقاط صورة
                    </button>
                  </div>

                  {/* Visit details form */}
                  <div className="border-t border-border pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Gate selector */}
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5 tracking-wide uppercase">البوابة</label>
                      <div className="relative">
                        <select
                          value={selectedGate}
                          onChange={(e) => setSelectedGate(e.target.value)}
                          className="w-full bg-input border border-border rounded-xl px-4 py-3 text-foreground text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                          {gates.map((g) => (
                            <option key={`gate-opt-${g}`} value={g}>{g}</option>
                          ))}
                        </select>
                        <ChevronDown size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                      </div>
                    </div>

                    {/* Purpose selector */}
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5 tracking-wide uppercase">غرض الزيارة</label>
                      <div className="relative">
                        <select
                          value={purpose}
                          onChange={(e) => setPurpose(e.target.value)}
                          className="w-full bg-input border border-border rounded-xl px-4 py-3 text-foreground text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                          <option value="">اختر الغرض...</option>
                          {purposes.map((p) => (
                            <option key={`purpose-opt-${p}`} value={p}>{p}</option>
                          ))}
                        </select>
                        <ChevronDown size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                      </div>
                    </div>

                    {/* Vehicle plate */}
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5 tracking-wide uppercase">رقم المركبة (اختياري)</label>
                      <div className="relative">
                        <Car size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="text"
                          value={vehiclePlate}
                          onChange={(e) => setVehiclePlate(e.target.value)}
                          placeholder="أ ب ج ١٢٣٤"
                          className="w-full bg-input border border-border rounded-xl pr-9 pl-4 py-3 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                      </div>
                    </div>

                    {/* Passengers */}
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5 tracking-wide uppercase">عدد الركاب</label>
                      <div className="relative">
                        <Users size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="number"
                          min="1"
                          max="20"
                          value={passengersCount}
                          onChange={(e) => setPassengersCount(e.target.value)}
                          className="w-full bg-input border border-border rounded-xl pr-9 pl-4 py-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring tabular-nums"
                        />
                      </div>
                    </div>

                    {/* Badge number */}
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5 tracking-wide uppercase">
                        رقم الكارت المُسلَّم <span className="text-danger">*</span>
                      </label>
                      <div className="relative">
                        <CreditCard size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="text"
                          value={badgeNumber}
                          onChange={(e) => setBadgeNumber(e.target.value)}
                          placeholder="B-045"
                          className="w-full bg-input border border-border rounded-xl pr-9 pl-4 py-3 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">رقم الكارت الجسدي المُسلَّم للزائر كأمانة</p>
                    </div>
                  </div>

                  {/* Safety acknowledgment */}
                  <div className={`border rounded-xl p-4 transition-all duration-200 ${safetyAcknowledged ? 'border-success/40 bg-success/5' : 'border-warning/40 bg-warning/5'}`}>
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => setSafetyAcknowledged(!safetyAcknowledged)}
                        suppressHydrationWarning
                        className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all duration-150 ${
                          safetyAcknowledged ? 'bg-success border-success' : 'border-warning bg-transparent'
                        }`}
                        aria-checked={safetyAcknowledged}
                        role="checkbox"
                      >
                        {safetyAcknowledged && <CheckCircle size={14} className="text-success-foreground" />}
                      </button>
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          إقرار السلامة المهنية <span className="text-danger">*</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                          يُقر الزائر بأنه اطلع على تعليمات السلامة، وسيلتزم بارتداء مهمات الوقاية الشخصية المطلوبة (خوذة — حذاء أمان — نظارة واقية)، وسيتقيد بكافة اشتراطات السلامة داخل المصنع.
                        </p>
                        {!safetyAcknowledged && (
                          <p className="text-xs text-warning font-medium mt-1.5 flex items-center gap-1">
                            <AlertTriangle size={10} />
                            يجب الموافقة على إقرار السلامة قبل السماح بالدخول
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Submit button */}
                  <button
                    onClick={handleRegisterEntry}
                    suppressHydrationWarning
                    disabled={isSubmitting || foundVisitor.isBlacklisted}
                    className={`w-full py-5 rounded-2xl font-bold text-xl flex items-center justify-center gap-3 transition-all duration-150 active:scale-[0.98] ${
                      isSubmitting
                        ? 'bg-accent/50 text-accent-foreground cursor-wait'
                        : 'bg-accent text-accent-foreground hover:opacity-90 pulse-green'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-6 h-6 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
                        جاري التسجيل...
                      </>
                    ) : (
                      <>
                        <LogIn size={22} />
                        تسجيل الدخول
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Exit Tab */}
          {activeTab === 'exit' && (
            <div className="bg-card border border-border rounded-2xl p-5">
              <h2 className="font-bold text-foreground text-lg mb-4">الموجودون داخل المصنع</h2>
              <p className="text-sm text-muted-foreground mb-4">اختر الزائر لتسجيل خروجه وتأكيد إعادة الكارت</p>

              <div className="space-y-2">
                {currentlyInside.map((visit) => (
                  <div
                    key={`exit-row-${visit.id}`}
                    className={`flex items-center gap-4 p-4 rounded-xl border transition-all duration-150 hover:border-accent/40 group ${
                      visit.status === 'منتهية الصلاحية' ?'bg-warning/5 border-warning/30' :'bg-muted/30 border-border'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                      <UserCheck size={18} className="text-accent" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-foreground text-sm truncate">{visit.visitorName}</p>
                        <StatusBadge status={visit.status} size="sm" />
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{visit.company} — {visit.hostDepartment}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock size={10} />
                          دخل: {formatTime(visit.entryTime)}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock size={10} />
                          المدة: {computeDuration(visit.entryTime)}
                        </span>
                        {visit.badgeNumber && (
                          <span className="flex items-center gap-1 text-xs text-accent">
                            <CreditCard size={10} />
                            {visit.badgeNumber}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setShowExitConfirm(visit)}
                      suppressHydrationWarning
                      className="opacity-0 group-hover:opacity-100 flex items-center gap-2 px-4 py-2 bg-danger/15 border border-danger/30 text-danger rounded-xl text-sm font-semibold hover:bg-danger/25 transition-all duration-150 active:scale-95 shrink-0"
                    >
                      <LogOut size={14} />
                      تسجيل خروج
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: Recent entries sidebar */}
        <div className="space-y-4">
          {/* Today's stats */}
          <div className="bg-card border border-border rounded-2xl p-4">
            <h3 className="font-bold text-foreground text-sm mb-3 flex items-center gap-2">
              <Shield size={14} className="text-accent" />
              إحصائيات اليوم
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted/50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-accent tabular-nums">
                  {mockVisits.filter(v => v.entryTime.startsWith('2026-08-09')).length}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">إجمالي الدخول</p>
              </div>
              <div className="bg-muted/50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-success tabular-nums">
                  {currentlyInside.filter(v => v.status === 'داخل').length}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">داخل الآن</p>
              </div>
              <div className="bg-muted/50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-warning tabular-nums">{overstayCount}</p>
                <p className="text-xs text-muted-foreground mt-0.5">منتهية الصلاحية</p>
              </div>
              <div className="bg-muted/50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-muted-foreground tabular-nums">
                  {mockVisits.filter(v => v.entryTime.startsWith('2026-08-09') && v.status === 'خرج').length}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">خرجوا</p>
              </div>
            </div>
          </div>

          {/* Recent entries */}
          <div className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-foreground text-sm flex items-center gap-2">
                <Clock size={14} className="text-accent" />
                آخر الدخول
              </h3>
              <button className="text-xs text-accent hover:text-accent/70 transition-colors flex items-center gap-1" suppressHydrationWarning>
                <Printer size={11} />
                طباعة
              </button>
            </div>
            <div className="space-y-2">
              {currentlyInside.slice(0, 6).map((visit) => (
                <div key={`recent-${visit.id}`} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                  <div className={`w-2 h-2 rounded-full shrink-0 ${visit.status === 'داخل' ? 'bg-success animate-pulse' : 'bg-warning'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground truncate">{visit.visitorName}</p>
                    <p className="text-xs text-muted-foreground truncate">{visit.company}</p>
                  </div>
                  <div className="text-left shrink-0">
                    <p className="text-xs font-semibold text-accent tabular-nums">{formatTime(visit.entryTime)}</p>
                    <p className="text-xs text-muted-foreground">{visit.gate.replace('البوابة ', '')}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Safety reminder */}
          <div className="bg-primary/30 border border-accent/20 rounded-2xl p-4">
            <h3 className="font-bold text-accent text-sm mb-2 flex items-center gap-2">
              <Shield size={14} />
              تذكير السلامة
            </h3>
            <ul className="space-y-1.5">
              {[
                'تحقق من إقرار السلامة قبل الدخول',
                'سجّل رقم الكارت المُسلَّم',
                'تحقق من قائمة الممنوعين',
                'التقط صورة للزائر والبطاقة',
              ].map((item, i) => (
                <li key={`reminder-${i}`} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <CheckCircle size={10} className="text-accent mt-0.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Safety modal */}
      {showSafetyModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm fade-in"
          onClick={() => setShowSafetyModal(false)}
        >
          <div
            className="bg-card border border-warning/40 rounded-2xl shadow-2xl w-full max-w-lg slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-border flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-warning/15 flex items-center justify-center">
                <AlertTriangle size={20} className="text-warning" />
              </div>
              <div>
                <h3 className="font-bold text-foreground text-lg">إقرار السلامة المهنية</h3>
                <p className="text-xs text-muted-foreground">يجب أن يوقع الزائر على هذا الإقرار قبل الدخول</p>
              </div>
              <button onClick={() => setShowSafetyModal(false)} suppressHydrationWarning className="mr-auto text-muted-foreground hover:text-foreground">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-warning/5 border border-warning/20 rounded-xl p-4">
                <h4 className="font-semibold text-foreground text-sm mb-3">تعليمات السلامة الإلزامية</h4>
                <ul className="space-y-2">
                  {[
                    'ارتداء خوذة الحماية في مناطق الإنتاج',
                    'ارتداء حذاء السلامة ذو المقدمة الفولاذية',
                    'ارتداء النظارة الواقية في مناطق الكيماويات',
                    'الالتزام بمسارات المشاة المحددة',
                    'الامتناع عن التدخين في كامل المنشأة',
                    'إبلاغ الأمن فور ملاحظة أي خطر',
                  ].map((rule, i) => (
                    <li key={`rule-${i}`} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="w-5 h-5 rounded-full bg-warning/20 text-warning text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                      {rule}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-muted/50 rounded-xl p-4">
                <p className="text-sm text-foreground leading-relaxed">
                  أنا الموقع أدناه أُقر بأنني اطلعت على جميع تعليمات السلامة المذكورة أعلاه وأتعهد بالالتزام بها طوال فترة وجودي داخل مصنع الأسمدة الكيماوية.
                </p>
              </div>
            </div>
            <div className="p-6 pt-0 flex gap-3">
              <button
                onClick={() => {
                  setSafetyAcknowledged(true);
                  setShowSafetyModal(false);
                  toast.success('تم تسجيل إقرار السلامة بنجاح');
                }}
                suppressHydrationWarning
                className="flex-1 py-3 bg-accent text-accent-foreground rounded-xl font-bold hover:opacity-90 transition-all active:scale-95"
              >
                أوافق على الإقرار
              </button>
              <button
                onClick={() => setShowSafetyModal(false)}
                suppressHydrationWarning
                className="flex-1 py-3 bg-muted text-foreground rounded-xl font-semibold hover:bg-muted/70 transition-all active:scale-95"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exit confirm modal */}
      <ConfirmModal
        isOpen={showExitConfirm !== null}
        title="تأكيد تسجيل الخروج"
        message={showExitConfirm ? `هل تأكد من تسجيل خروج ${showExitConfirm.visitorName}؟ تأكد من استلام الكارت رقم ${showExitConfirm.badgeNumber} قبل المتابعة.` : ''}
        confirmLabel="تأكيد الخروج واستلام الكارت"
        cancelLabel="إلغاء"
        variant="warning"
        onConfirm={() => showExitConfirm && handleConfirmExit(showExitConfirm)}
        onCancel={() => setShowExitConfirm(null)}
      />
    </div>
  );
}